import React, { useState, useEffect } from "react";
import Header from "../../../Components/Header";
import Seo from "../../../Components/SeoComponent/Seo";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { verifyKybGstToContactService } from "../../../Services/ApiTestService/apiTestService";
import { InputWithLabel, Button } from "../../../Components/Tags/Tags";
import { FiCheckCircle } from 'react-icons/fi';
import Loader from "../../../Loader";
import { Link } from "react-router-dom";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { FaCopy, FaUser } from "react-icons/fa";
import { useService } from "../../../context/ServiceContext";

const VerifyKybGSTToContact = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [gstData, setGstData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [profileData, setProfileData] = useState({});
  const [gst, setGst] = useState("");
  const { serviceName } = useService();

  // GST Number validation regex - Format: 2 digits (state) + 10 chars (PAN) + 1 char + 1 char/digit + Z + 1 char/digit
  const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;

  // Fetch profile data on mount
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const response = await fetchProfile();
        setProfileData(response?.data ?? {});
      } catch (error) {
        console.error("Error fetching profile data:", error);
        showErrorToast("Failed to fetch profile data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const validateGST = (gstNumber) => {
    if (!gstNumber) {
      return { isValid: false, message: "GST number is required" };
    }
    
    const trimmedGST = gstNumber.trim().toUpperCase();
    
    if (trimmedGST.length !== 15) {
      return { isValid: false, message: "GST number must be exactly 15 characters" };
    }
    
    if (!GST_REGEX.test(trimmedGST)) {
      return { isValid: false, message: "Invalid GST number format" };
    }
    
    return { isValid: true, message: "Valid GST format" };
  };




// Handle Submit Function
const handleSubmit = async () => {
  const validation = validateGST(gst);
  if (!validation.isValid) {
    showErrorToast(validation.message);
    return;
  }

  try {
    setIsLoading(true);
    const payload = {
      api_id: profileData?.user_id,
      api_key: profileData?.api_key,
      token_id: profileData?.token_id,
      gstin: gst.trim().toUpperCase(),
    };
    
    const response = await verifyKybGstToContactService(payload);
    
    if (response?.status?.code === 200) {
      // Set both data and full response
      setGstData(response?.data ?? null);
      setResponseData(response);
      setGst('');
      showSuccessToast(
        response?.message ?? 
        response?.status?.message ?? 
        "GST Contact Details Fetched Successfully"
      );
    } else {
      setGstData(null);
      setResponseData(null);
      showErrorToast(
        response?.message ?? 
        response?.status?.message ?? 
        response?.errors?.message ?? 
        "Failed to fetch GST contact details."
      );
    }
  } catch (error) {
    console.error("GST Verification Error:", error);
    showErrorToast("Failed to verify GST details. Please try again.");
    setGstData(null);
    setResponseData(null);
  } finally {
    setIsLoading(false);
  }
};


  

  function handleCopyText(data) {
    if (data) {
      window.navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      showSuccessToast('Copied successfully');
    }
  }





  return (
    <>
      <Seo />
      {isLoading && <Loader />}
      <Header heading={serviceName} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 justify-center md:ml-80 ml-0 p-4">
        <div className="col-span-2 md:col-span-6 flex justify-end">
          <Link
            to="/TestMaster"
            className="flex items-center gap-1 text-white bg-green-600 hover:bg-green-700 px-3 py-2 rounded-md font-semibold shadow-md transition duration-300"
          >
            <span>Go Back</span>
            <IoArrowBackCircleOutline size={24} />
          </Link>
        </div>

        <InputWithLabel
          type="text"
          label="GST Number (GSTIN)"
          placeholder="Enter 15-digit GST Number"
          maxLength="15"
          className="p-2 rounded shadow-lg shadow-blue-500/50 border border-blue-600 uppercase"
          name="gst"
          id="gst"
          value={gst}
          required={true}
          onChange={(e) => {
            const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            setGst(value);
          }}
        />

        <div>
          <Button
            type="button"
            className="text-center text-white bg-blue-900 shadow-lg bg-gradient-to-r from-blue-400 via-blue-600 to-blue-900 font-semibold rounded-md px-4 py-2 mt-5 text-md hover:from-blue-900 hover:to-blue-400 border-2 ms-2 disabled:opacity-50 disabled:cursor-not-allowed"
            id="verifyGST"
            name="verifyGST"
            text="Verify"
            onClick={handleSubmit}
            disabled={!gst || gst.length !== 15}
          />
        </div>
      </div>

 

{gstData !== null && (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-4">
    {/* Main Details Card */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">
        Contact Details
      </h3>
      <div className="space-y-4 text-sm">
        {/* Mobile */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <div className="flex-grow">
            <p className="text-xs text-gray-500 font-medium">Mobile Number</p>
            <p className="text-base text-gray-800 font-semibold">
              {gstData?.mobile || 'N/A'}
            </p>
          </div>
        </div>

        {/* Email */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex-grow">
            <p className="text-xs text-gray-500 font-medium">Email Address</p>
            <p className="text-base text-gray-800 font-semibold break-all">
              {gstData?.email || 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* API Response */}
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <h3 className="bg-green-500 text-white text-xl font-bold p-4 flex items-center justify-between">
        <span>API Response</span>
        <FaCopy 
          className="cursor-pointer bg-white text-green-500 p-2 rounded-md text-3xl hover:bg-green-50 transition"
          onClick={() => handleCopyText(responseData)} 
          title="Copy Response"
        />
      </h3>
      <pre className="bg-gray-900 text-green-400 p-4 text-xs overflow-auto max-h-[400px] custom-scrollbar font-mono">
        {JSON.stringify(responseData, null, 2)}
      </pre>
    </div>
  </div>
)}



      <GstInstructions />
    </>
  );
};

const GstInstructions = () => {
  return (
    <div className="max-w-5xl mx-auto p-5 my-6 bg-white shadow-lg rounded-lg border border-gray-200">
      <h1 className="text-xl font-semibold mb-4 text-red-600 border-b pb-2">
        GST to Contact - Instructions
      </h1>
      <ol className="list-decimal pl-5 space-y-3 text-gray-700 text-sm">
        <li>
          <strong>GST Number Format:</strong>
          <ul className="list-disc pl-5 mt-2 text-gray-600 space-y-1">
            <li>GST number must be exactly 15 characters long</li>
            <li>Format: 2 digits (State Code) + 10 characters (PAN) + 3 characters</li>
            <li>Example: 27AAPFU0939F1ZV (Maharashtra state)</li>
          </ul>
        </li>
        <li>
          <strong>Important Guidelines:</strong>
          <ul className="list-disc pl-5 mt-2 text-gray-600 space-y-1">
            <li>Enter a valid registered GST number only</li>
            <li>Incorrect GST numbers will result in charge deduction without results</li>
            <li>Do not include spaces or special characters</li>
            <li>Do not close or reload the page during processing</li>
          </ul>
        </li>
        <li>
          <strong>GST Number Structure:</strong>
          <ul className="list-disc pl-5 mt-2 text-gray-600 space-y-1">
            <li>Characters 1-2: State code (01-37)</li>
            <li>Characters 3-12: PAN number of the business</li>
            <li>Character 13: Registration number within state</li>
            <li>Character 14: Default 'Z'</li>
            <li>Character 15: Checksum character</li>
          </ul>
        </li>
        <li>
          <strong>Troubleshooting:</strong>
          <ul className="list-disc pl-5 mt-2 text-gray-600 space-y-1">
            <li>Double-check the GST number before submitting</li>
            <li>Ensure the GST number is active and registered</li>
            <li>Verify the format matches the 15-character pattern</li>
            <li>Contact support if issues persist</li>
          </ul>
        </li>
      </ol>
    </div>
  );
};

export default VerifyKybGSTToContact;
